import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useAuthContext } from '../contexts/AuthContext'
import {
  clearAllGeneratedMedia,
  deleteSingleMedia,
  getMediaInfo,
  listMediaAssets,
} from '../services/generationService'

const formatSize = (bytes = 0) => {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  let value = bytes
  let i = 0
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024
    i += 1
  }
  return `${value.toFixed(i === 0 ? 0 : 2)} ${units[i]}`
}

const timeAgo = iso => {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'adesso'
  if (mins < 60) return `${mins} min fa`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h fa`
  const days = Math.floor(hrs / 24)
  return `${days}g fa`
}

const ChatAssetsPage = () => {
  const { token } = useAuthContext()
  const [loading, setLoading] = useState(true)
  const [clearing, setClearing] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [mediaInfo, setMediaInfo] = useState(null)
  const [assets, setAssets] = useState([])
  const [preview, setPreview] = useState(null)
  const [deletingKey, setDeletingKey] = useState(null)

  const loadData = useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError('')
    try {
      const [info, list] = await Promise.all([
        getMediaInfo(token),
        listMediaAssets(token),
      ])
      setMediaInfo(info)
      setAssets(list?.items || [])
    } catch (err) {
      setError(err?.response?.data?.detail || 'Impossibile caricare gli assets.')
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleClearAll = async () => {
    if (!token) return
    setClearing(true)
    setError('')
    setSuccess('')
    try {
      const res = await clearAllGeneratedMedia(token)
      setSuccess(res?.message || 'Assets eliminati con successo.')
      await loadData()
    } catch (err) {
      setError(err?.response?.data?.detail || 'Errore durante eliminazione assets.')
    } finally {
      setClearing(false)
    }
  }

  const handleDeleteSingle = async (filename) => {
    if (!token || !filename) return
    setDeletingKey(filename)
    setError('')
    try {
      await deleteSingleMedia(token, filename)
      setAssets(prev => prev.filter(a => a.filename !== filename))
      setMediaInfo(prev =>
        prev ? { ...prev, file_count: Math.max(0, (prev.file_count || 1) - 1) } : prev
      )
      if (preview?.filename === filename) setPreview(null)
    } catch (err) {
      setError(err?.response?.data?.detail || 'Impossibile eliminare il file.')
    } finally {
      setDeletingKey(null)
    }
  }

  const imageAssets = assets.filter(a => a.type === 'image')
  const otherAssets = assets.filter(a => a.type !== 'image')

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'linear-gradient(to bottom left, #ccd9ed 0%, #ffffff 50%)',
        boxSizing: 'border-box',
        overflow: 'auto',
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 900, width: '100%', mx: 'auto' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
          <PermMediaOutlinedIcon
            sx={{ color: 'var(--color-primary-main, #1e3666)', fontSize: 28 }}
          />
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ color: 'var(--text-primary, #1e293b)' }}
          >
            Assets Chat
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: 'var(--text-secondary, #64748b)', mb: 1 }}>
          Media generati automaticamente durante le spiegazioni in chat.
        </Typography>

        {!loading && mediaInfo && (
          <Stack
            direction="row"
            spacing={3}
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
              {mediaInfo.file_count} file &middot; {formatSize(mediaInfo.total_size_bytes)}
              &nbsp;&middot;&nbsp; Scadenza auto: {mediaInfo.retention_days}g
            </Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Aggiorna">
                <IconButton size="small" onClick={loadData} disabled={loading || clearing}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {assets.length > 0 && (
                <Button
                  color="error"
                  variant="text"
                  size="small"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleClearAll}
                  disabled={loading || clearing}
                  sx={{ textTransform: 'none' }}
                >
                  {clearing ? 'Eliminazione...' : 'Elimina tutti'}
                </Button>
              )}
            </Stack>
          </Stack>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
              Caricamento...
            </Typography>
          </Stack>
        ) : assets.length === 0 ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
            <PermMediaOutlinedIcon
              sx={{ fontSize: 56, color: 'var(--color-neutral-400, #a8a8aa)' }}
            />
            <Typography
              variant="body1"
              sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}
            >
              Nessun asset generato
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'var(--text-secondary)',
                textAlign: 'center',
                maxWidth: 380,
              }}
            >
              Le immagini create durante le spiegazioni in chat appariranno qui. Vengono
              eliminate automaticamente dopo {mediaInfo?.retention_days ?? 3} giorni.
            </Typography>
          </Stack>
        ) : (
          <>
            {imageAssets.length > 0 && (
              <ImageList
                cols={3}
                gap={12}
                sx={{
                  mt: 0,
                  '& .MuiImageListItem-root': {
                    borderRadius: 'var(--radius-lg, 12px)',
                    overflow: 'hidden',
                    boxShadow: 'var(--shadow-sm, 0 1px 2px rgba(0,0,0,0.05))',
                    border: '1px solid var(--border-color, #e2e8f0)',
                    cursor: 'pointer',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 'var(--shadow-md)',
                    },
                  },
                }}
              >
                {imageAssets.map(asset => (
                  <ImageListItem key={asset.key}>
                    <img
                      src={asset.url}
                      alt={asset.filename}
                      loading="lazy"
                      onClick={() => setPreview(asset)}
                      style={{
                        objectFit: 'cover',
                        width: '100%',
                        aspectRatio: '1',
                        display: 'block',
                        background: '#f1f5f9',
                      }}
                    />
                    <ImageListItemBar
                      sx={{
                        background:
                          'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%)',
                        '& .MuiImageListItemBar-title': { fontSize: 11 },
                        '& .MuiImageListItemBar-subtitle': { fontSize: 10 },
                      }}
                      title={formatSize(asset.size_bytes)}
                      subtitle={timeAgo(asset.last_modified)}
                      actionIcon={
                        <Tooltip title="Elimina">
                          <IconButton
                            size="small"
                            sx={{ color: 'rgba(255,255,255,0.8)' }}
                            onClick={e => {
                              e.stopPropagation()
                              handleDeleteSingle(asset.filename)
                            }}
                            disabled={deletingKey === asset.filename}
                          >
                            {deletingKey === asset.filename ? (
                              <CircularProgress size={14} sx={{ color: 'white' }} />
                            ) : (
                              <DeleteOutlineIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      }
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}

            {otherAssets.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ color: 'var(--text-secondary)', mb: 1 }}
                >
                  Altri file ({otherAssets.length})
                </Typography>
                <Stack spacing={1}>
                  {otherAssets.map(asset => (
                    <Stack
                      key={asset.key}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{
                        p: 1.5,
                        borderRadius: 'var(--radius-md, 8px)',
                        border: '1px solid var(--border-color, #e2e8f0)',
                        background: 'white',
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {asset.filename}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}
                      >
                        {formatSize(asset.size_bytes)} &middot;{' '}
                        {timeAgo(asset.last_modified)}
                      </Typography>
                      <Tooltip title="Apri">
                        <IconButton
                          size="small"
                          href={asset.url}
                          target="_blank"
                          rel="noopener"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Elimina">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteSingle(asset.filename)}
                          disabled={deletingKey === asset.filename}
                        >
                          {deletingKey === asset.filename ? (
                            <CircularProgress size={14} />
                          ) : (
                            <DeleteOutlineIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
          </>
        )}
      </Box>

      <Dialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 'var(--radius-xl, 16px)',
              overflow: 'hidden',
              background: '#000',
            },
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', justifyContent: 'center' }}>
          <IconButton
            onClick={() => setPreview(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.4)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
          {preview && (
            <img
              src={preview.url}
              alt={preview.filename}
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#111', justifyContent: 'space-between', px: 2 }}>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            {preview?.filename} &middot; {formatSize(preview?.size_bytes)}{' '}
            &middot; {timeAgo(preview?.last_modified)}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              href={preview?.url}
              target="_blank"
              rel="noopener"
              sx={{ color: '#ddd', textTransform: 'none' }}
              startIcon={<OpenInNewIcon />}
            >
              Apri originale
            </Button>
            <Button
              size="small"
              color="error"
              onClick={() => preview && handleDeleteSingle(preview.filename)}
              disabled={deletingKey === preview?.filename}
              sx={{ textTransform: 'none' }}
              startIcon={<DeleteOutlineIcon />}
            >
              Elimina
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ChatAssetsPage
